import { useEffect, useRef } from 'react';

// ============================================================
// useAutoRefresh — keeps a portal in sync with the shared store
// (the dev API / emulator) so admin updates reflect without a
// manual reload. Re-runs `onRefresh`:
//   - when the app/tab regains focus or becomes visible, and
//   - on a light interval (default 30s), but only while visible
//     so a hidden/background tab makes no needless requests.
//
// The callback is held in a ref so listeners/interval are set up
// once and always call the latest closure.
// ============================================================

interface Options {
  intervalMs?: number;
  enabled?: boolean;
}

export function useAutoRefresh(onRefresh: () => void, opts: Options = {}): void {
  const { intervalMs = 30000, enabled = true } = opts;
  const cb = useRef(onRefresh);
  cb.current = onRefresh;

  useEffect(() => {
    if (!enabled) return;

    const run = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      cb.current();
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') cb.current();
    };

    window.addEventListener('focus', run);
    document.addEventListener('visibilitychange', onVisible);
    const timer = window.setInterval(run, intervalMs);

    return () => {
      window.removeEventListener('focus', run);
      document.removeEventListener('visibilitychange', onVisible);
      window.clearInterval(timer);
    };
  }, [intervalMs, enabled]);
}
